import { defineStore } from 'pinia';
import type { Breadcrumb } from '@experience/core-breadcrumbs-ui';
import { trackEvent } from '@/services/analytics';
import { formatDate } from '@experience/datetime-utils';
import {
  type ExperienceFavoriteItem,
  type ExperiencesListItem,
  getCalendarEventsChips,
  type GetCalendarEventsChipsResponse,
  getCountryInfoBySlug,
  getExperienceAggregations,
  getExperiencesFavoritesList,
  getExperiencesList,
  getSeoCategoryBySlug,
  getSeoCategoryInfoById,
  getToursActivities,
  type ReviewApiFilter,
} from '@/shared/api/experience';
import { ListSortItemValue, type SeoCategory } from '@/shared/models';
import { camelize } from '@/shared/utils/camelize';
import { useLogger } from '@/shared/utils/logger';
import {
  DEFAULT_EXPERIENCE_FILTERS,
  EExperienceFilterType,
  getFiltersDefaultState,
  type IExperienceFilter,
  type IExperienceQueryParams,
} from '@/widgets/filtersBlock';
import { isSorting, mapExperienceData } from '@/widgets/experiencesList';
import { computed, ref, reactive } from 'vue';
import { useRoute } from 'vue-router';
import type { Experience } from '@experience/exp-card/dist/types/experience';
import { createToursActivitiesFromResource } from '@/entities/tour-activities/model/mapToursActivities';
import { useToursActivitiesLabels } from '@/entities/tour-activities/composable/useTourActivitiesLabels';
import type { ToursActivity } from '@/entities/tour-activities';
import { DAY_HOURS } from '@/features/filter/duration/const/config';
import { COMFORT_VALUE, type TComfortValue } from '@/features/filter';
import { type TAccommodationValue } from '@/features/filter/accommodation';
import type { TActivityLevelValue } from '@/features/filter/activity-level';
import { deepCopy } from '../lib';

export const useCategoryStore = defineStore('category-store', () => {
  /**
   * [boolean] начат процесс инициализации стора?
   */
  const isInitialised = ref(false);
  const isInitialising = ref(false);
  /**
   * [boolean] флаг активности загрузки списка Э
   */
  const isExperiencesLoading = ref(false);
  const isExperiencesCountLoading = ref(false);
  /**
   * [boolean] флаг активности скелетона
   */
  const isSkeletonActive = ref(true);
  /**
   * [number] кол-ва Э на странице
   */
  const PAGE_SIZE = 12;
  /**
   *
   */
  const experiences = ref<Experience[]>([]);
  const experiencesFavorites = ref<ExperienceFavoriteItem[]>([]);
  const experiencesCount = ref(0);
  const experiencesTmpCount = ref(0);
  const pageCount = computed(() => Math.ceil(experiencesCount.value / PAGE_SIZE));
  /**
   *
   */
  const route = useRoute();
  const countrySlug = computed(() => String(route.params.countrySlug ?? ''));
  const categorySlug = computed(() => String(route.params.categorySlug ?? ''));
  const currentRouteKey = computed(() => `${countrySlug.value}:${categorySlug.value}`);
  const appliedRouteKey = ref('');

  const breadcrumbs = ref<Breadcrumb[]>([]);

  const reviewsApiFilters = ref<ReviewApiFilter>(getFiltersDefaultState()[EExperienceFilterType.SEO_DEFAULT]);
  /**
   * [number] кол-во примененных фильтров
   */
  const appliedFiltersCount = ref(0);
  const appliedTmpFiltersCount = ref(0);
  /**
   * [ref] оригинал фильтров
   */
  const filters: IExperienceFilter = reactive(getFiltersDefaultState());
  /**
   * [ref] копия фильтров (для отображения кол-ва элементов в фильтре)
   */
  const tmpFilters: IExperienceFilter = reactive(getFiltersDefaultState());
  /**
   *
   */
  const seoCategoryId = ref(0);
  const seoCategoryTitle = ref('');
  const seoData = ref<{
    h1: string;
    h2: string;
    title: string;
    description: string;
    text: string;
    img: string;
    canonicalUrl: string;
  }>({
    h1: '',
    h2: '',
    title: '',
    description: '',
    text: '',
    img: '',
    canonicalUrl: '',
  });
  /**
   *
   */
  const logger = useLogger();
  /**
   *
   */
  const __redirect = () => {
    window.location.href = '/';
  };
  /**
   * [method] Загружаем SEO-meta-информацию страницы
   */
  const loadSeoInfo = async () => {
    try {
      const { data } = await getSeoCategoryInfoById({
        query: {
          ids: `${seoCategoryId.value}`,
        },
      });

      const seoInfo = data;

      seoData.value = {
        h1: seoInfo?.seo?.h1 || '',
        h2: seoInfo?.seo?.h2 || '',
        title: seoInfo?.seo?.title || '',
        description: seoInfo?.seo?.description || '',
        text: seoInfo?.seo?.text || '',
        img: 'https://experience.tripster.ru/static/i/tripster-share-logo.jpg',
        canonicalUrl: seoData.value.canonicalUrl,
      };
    } catch (e) {
      logger.error(e);
      __redirect();
    }
  };
  /**
   *
   */
  const loadSeoCategory = async () => {
    try {
      const requestCountrySlug = countrySlug.value;
      const requestCategorySlug = categorySlug.value;
      const { data } = await getSeoCategoryBySlug({ path: { categorySlug: requestCategorySlug } });

      if (!data) {
        __redirect();

        return;
      }

      // TODO: Заменить использование camelize на более точный нормализатор для конкретного типа, учитывающий
      // возможное присутствие двойного подчеркивания
      const seoCategory: SeoCategory = camelize(data);

      /**
       * Если слаг страны не тот - редиректим
       */
      if (requestCountrySlug !== seoCategory?.countrySlug) {
        __redirect();

        return;
      }

      if (requestCountrySlug !== countrySlug.value || requestCategorySlug !== categorySlug.value) {
        return;
      }

      seoCategoryId.value = Number(seoCategory?.id);
      seoCategoryTitle.value = seoCategory?.categoryTitle;

      filters[EExperienceFilterType.SEO_DEFAULT] = seoCategory?.experiencesApiFilters;
      tmpFilters[EExperienceFilterType.SEO_DEFAULT] = seoCategory?.experiencesApiFilters;
      reviewsApiFilters.value = seoCategory?.reviewsApiFilters;

      const finalBreadcrumbs: Breadcrumb[] = [
        { label: 'Главная', to: `/` },
        { label: 'Все туры', to: '/tours/' },
      ];

      if (seoCategory.countryRu && seoCategory.countrySlug) {
        finalBreadcrumbs.push({ label: seoCategory.countryRu, to: `/tours/${seoCategory.countrySlug}` });
      }

      finalBreadcrumbs.push({ label: seoCategory.categoryTitle, to: '', schemaOnly: true });

      breadcrumbs.value = finalBreadcrumbs;

      trackEvent('category.pageview', {
        category_title: seoCategoryTitle.value,
        category_id: +seoCategoryId.value,
      });
    } catch (e) {
      logger.error('could not load a seo category by the reason:', e);
      __redirect();
    }
  };
  /**
   * [number] номер текущей страницы
   */
  const currentPage = ref(1);
  /**
   * [] текущая сортировка
   */
  const currentSorting = ref(ListSortItemValue.RANK);
  /**
   *
   */
  const __checkFilterIsEmpty = (filterKey: EExperienceFilterType | EExperienceFilterType[], f: IExperienceFilter) => {
    switch (filterKey) {
      case EExperienceFilterType.PERIOD:
        return f[EExperienceFilterType.PERIOD].start_date === null && f[EExperienceFilterType.PERIOD].end_date === null;
      case EExperienceFilterType.TOUR_ACTIVITY_SLUGS:
        return f[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.length === 0;
      case EExperienceFilterType.DURATION:
        return (
          f[EExperienceFilterType.DURATION].duration[0] === 0 && f[EExperienceFilterType.DURATION].duration[1] === 0
        );
      case EExperienceFilterType.PRICE:
        return f[EExperienceFilterType.PRICE].price[0] === null && f[EExperienceFilterType.PRICE].price[1] === null;
      case EExperienceFilterType.PERSONS:
        return f[EExperienceFilterType.PERSONS].persons <= 1;
      case EExperienceFilterType.COMFORT_TYPE:
        return f[EExperienceFilterType.COMFORT_TYPE].comfort_type.length === 0;
      case EExperienceFilterType.ACCOMMODATION:
        return f[EExperienceFilterType.ACCOMMODATION].accommodation.length === 0;
      case EExperienceFilterType.ACTIVITY_LEVEL:
        return f[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.length === 0;
      case EExperienceFilterType.CHILDREN_ALLOWED:
        return f[EExperienceFilterType.CHILDREN_ALLOWED].children_allowed === false;
      default:
        return true;
    }
  };
  /**
   *
   */
  const __countAppliedFilters = (f: IExperienceFilter) => {
    let count = 0;

    Object.values(EExperienceFilterType).forEach((fKey) => {
      if (!__checkFilterIsEmpty(fKey, f)) {
        count += 1;
      }
    });

    return count;
  };
  /**
   * [method] сформировать параметры query для запроса
   */
  const __getRequestQuery = (f: IExperienceFilter, withPagination: boolean = true, isLoadMore: boolean = false) => {
    const query: IExperienceQueryParams = {
      ...f[EExperienceFilterType.SEO_DEFAULT],
      sorting: currentSorting.value,
    };

    appliedTmpFiltersCount.value = __countAppliedFilters(tmpFilters);

    if (withPagination) {
      query.offset = (currentPage.value - 1) * PAGE_SIZE;
      query.limit = isLoadMore ? PAGE_SIZE + 1 : PAGE_SIZE;
      /**
       * если с паджинацией - то идет запрос списка, а значит фильтры уже применены
       */
      appliedFiltersCount.value = __countAppliedFilters(f);
    }

    if (f[EExperienceFilterType.PERIOD].start_date) {
      query.start_date = f[EExperienceFilterType.PERIOD].start_date;
      query.date_filter_mode = 'event_date_range';
    }

    if (f[EExperienceFilterType.PERIOD].end_date) {
      query.end_date = f[EExperienceFilterType.PERIOD].end_date;
    }

    if (f[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.length) {
      query.tour_activity_slugs = f[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.join(',');
    }

    if (f[EExperienceFilterType.DURATION].duration[0]) {
      query.start_duration = f[EExperienceFilterType.DURATION].duration[0] * DAY_HOURS;
    }

    if (f[EExperienceFilterType.DURATION].duration[1]) {
      query.end_duration = f[EExperienceFilterType.DURATION].duration[1] * DAY_HOURS;
    }

    if (f[EExperienceFilterType.PRICE].price[0]) {
      query.start_price = f[EExperienceFilterType.PRICE].price[0];
    }

    if (f[EExperienceFilterType.PRICE].price[1]) {
      query.end_price = f[EExperienceFilterType.PRICE].price[1];
    }

    if (f[EExperienceFilterType.PERSONS].persons > 1) {
      query.persons_count = f[EExperienceFilterType.PERSONS].persons;
    }

    if (f[EExperienceFilterType.COMFORT_TYPE].comfort_type.length) {
      query.comfort_type = f[EExperienceFilterType.COMFORT_TYPE].comfort_type.join(',');
    }

    if (f[EExperienceFilterType.ACCOMMODATION].accommodation.length) {
      query.accommodation_type = f[EExperienceFilterType.ACCOMMODATION].accommodation.join(',');
    }

    if (f[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.length) {
      query.activity_level_category = f[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.join(',');
    }

    if (f[EExperienceFilterType.CHILDREN_ALLOWED].children_allowed) {
      query.children_allowed = f[EExperienceFilterType.CHILDREN_ALLOWED].children_allowed;
    }

    return query;
  };
  /**
   * [method] Загрузка списка экскурсий/туров
   */
  const loadExperiences = async (isLoadMore?: boolean) => {
    if (isExperiencesLoading.value) {
      return;
    }
    isExperiencesLoading.value = true;

    if (!isLoadMore && currentPage.value !== 1) {
      /**
       * скрол до рубрик
       */
      rubricsRef.value?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    try {
      const [experiencesFavoritesData, experiencesData] = await Promise.all([
        getExperiencesFavoritesList(),
        getExperiencesList({
          query: { ...__getRequestQuery(filters, true, isLoadMore) },
        }),
      ]);

      experiencesFavorites.value = experiencesFavoritesData?.data || [];

      const { data } = experiencesData;

      if (!data) {
        return;
      }

      const { results } = data;

      const mapResult = results?.map((item: ExperiencesListItem) => mapExperienceData(item));

      experiences.value = isLoadMore ? [...experiences.value, ...mapResult] : mapResult;

      experiencesCount.value = data.count;
      experiencesTmpCount.value = data.count;
    } catch (e) {
      logger.error(e);
    } finally {
      isExperiencesLoading.value = false;
    }
  };
  /**
   * [method] Загрузка счетчика экскурсий/туров
   */
  const loadExperiencesCount = async () => {
    if (isExperiencesCountLoading.value || !isInitialised.value) {
      return;
    }
    isExperiencesCountLoading.value = true;

    try {
      const data = await getExperiencesList({
        query: { ...__getRequestQuery(tmpFilters, false) },
      });

      if (!data.data) {
        return;
      }

      experiencesTmpCount.value = data.data.count;
    } catch (e) {
      logger.error(e);
    } finally {
      isExperiencesCountLoading.value = false;
    }
  };
  /**
   *
   */
  const resetLists = async () => {
    currentPage.value = 1;

    await loadExperiences();
  };
  /**
   * [method] setter для фильтра Period
   */
  const setPeriod = async (start_date: string | null, end_date: string | null) => {
    tmpFilters[EExperienceFilterType.PERIOD].start_date = start_date;
    tmpFilters[EExperienceFilterType.PERIOD].end_date = end_date;

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Format
   */
  const setTourActivitySlugs = async (tour_activity_slugs: string[]) => {
    tmpFilters[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.splice(
      0,
      tmpFilters[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.length,
    );
    tour_activity_slugs.forEach((slug) => {
      tmpFilters[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs.push(slug);
    });

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Duration
   */
  const setDuration = async (durationFrom: number, durationTo: number) => {
    tmpFilters[EExperienceFilterType.DURATION].duration = [durationFrom, durationTo];

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Price
   */
  const setPrice = async (price: [number, number]) => {
    tmpFilters[EExperienceFilterType.PRICE].price = price;

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Persons
   */
  const setPersons = async (persons: number) => {
    tmpFilters[EExperienceFilterType.PERSONS].persons = persons;

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Comfort Type
   */
  const setComfortType = async (comfort_type: TComfortValue[]) => {
    tmpFilters[EExperienceFilterType.COMFORT_TYPE].comfort_type.splice(
      0,
      tmpFilters[EExperienceFilterType.COMFORT_TYPE].comfort_type.length,
    );
    comfort_type.forEach((type) => {
      tmpFilters[EExperienceFilterType.COMFORT_TYPE].comfort_type.push(type);
    });

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Accommodation
   */
  const setAccommodation = async (accommodation: TAccommodationValue[]) => {
    tmpFilters[EExperienceFilterType.ACCOMMODATION].accommodation.splice(
      0,
      tmpFilters[EExperienceFilterType.ACCOMMODATION].accommodation.length,
    );
    accommodation.forEach((type) => {
      tmpFilters[EExperienceFilterType.ACCOMMODATION].accommodation.push(type);
    });

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Activity Level
   */
  const setActivityLevel = async (activity_level: TActivityLevelValue[]) => {
    tmpFilters[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.splice(
      0,
      tmpFilters[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.length,
    );
    activity_level.forEach((type) => {
      tmpFilters[EExperienceFilterType.ACTIVITY_LEVEL].activity_level.push(type);
    });

    await loadExperiencesCount();
  };
  /**
   * [method] setter для фильтра Children Allowed
   */
  const setChildrenAllowed = async (children_allowed: boolean) => {
    tmpFilters[EExperienceFilterType.CHILDREN_ALLOWED].children_allowed = children_allowed;

    await loadExperiencesCount();
  };
  /**
   * [method] setter для сортировки
   */
  const setSorting = async (value: ListSortItemValue) => {
    currentSorting.value = value;

    const queryParams = new URLSearchParams(window.location.search);
    if (value === ListSortItemValue.RANK) {
      queryParams.delete('sorting');
    } else {
      queryParams.set('sorting', `${value}`);
    }

    history.replaceState(null, window.location.origin, `?${queryParams.toString()}`);

    trackEvent('category:sorting.apply', {
      category_title: seoCategoryTitle.value,
      category_id: seoCategoryId.value,
      sorting_name: value,
    });

    await resetLists();
  };
  /**
   * [method] setter для страницы
   */
  const setPage = async (value: number) => {
    if (isExperiencesLoading.value) {
      return;
    }

    currentPage.value = value;

    const queryParams = new URLSearchParams(window.location.search);
    if (value === 1) {
      queryParams.delete('page');
    } else {
      queryParams.set('page', `${value}`);
    }
    await loadExperiences();
  };
  /**
   * [method]
   */
  const loadMore = async () => {
    if (isExperiencesLoading.value) {
      return;
    }

    trackEvent('category:show_more.click', {
      category_title: seoCategoryTitle.value,
      category_id: seoCategoryId.value,
    });

    currentPage.value = currentPage.value + 1;

    await loadExperiences(true);
  };
  /**
   * [method] применить фильтр
   */
  const onApplyFilter = async (filterKey: EExperienceFilterType | EExperienceFilterType[]) => {
    const queryParams = new URLSearchParams(window.location.search);

    if (Array.isArray(filterKey)) {
      filterKey.forEach((fKey) => {
        for (const [key, value] of Object.entries(tmpFilters[fKey])) {
          filters[fKey][key] = deepCopy(value);

          if (__checkFilterIsEmpty(fKey, filters)) {
            queryParams.delete(`${fKey}.${key}`);
          } else {
            queryParams.set(`${fKey}.${key}`, `${filters[fKey][key]}`);
          }
        }
      });
    } else {
      for (const [key, value] of Object.entries(tmpFilters[filterKey])) {
        filters[filterKey][key] = deepCopy(value);

        if (__checkFilterIsEmpty(filterKey, filters)) {
          queryParams.delete(`${filterKey}.${key}`);
        } else {
          queryParams.set(`${filterKey}.${key}`, `${filters[filterKey][key]}`);
        }
      }
    }

    history.replaceState(null, window.location.origin, `?${queryParams.toString()}`);

    await resetLists();
  };
  /**
   *
   */
  const __resetFilter = async (
    filterKey: EExperienceFilterType | EExperienceFilterType[],
    sourceFilters: IExperienceFilter,
  ) => {
    if (Array.isArray(filterKey)) {
      filterKey.forEach((fKey) => {
        for (const [key, value] of Object.entries(sourceFilters[fKey])) {
          tmpFilters[fKey][key] = deepCopy(value);
        }
      });
    } else {
      for (const [key, value] of Object.entries(sourceFilters[filterKey])) {
        tmpFilters[filterKey][key] = deepCopy(value);
      }
    }

    appliedTmpFiltersCount.value = __countAppliedFilters(tmpFilters);
  };
  /**
   * [method] сбросить фильтр (сбрасываем только временные фильтры)
   */
  const onResetFilter = async (filterKey: EExperienceFilterType | EExperienceFilterType[]) => {
    await __resetFilter(filterKey, DEFAULT_EXPERIENCE_FILTERS);

    await loadExperiencesCount();
  };
  /**
   * [method] сброс временных фильтров (если модалку просто закрыли)
   */
  const onResetTmpFilter = async (filterKey: EExperienceFilterType | EExperienceFilterType[]) => {
    await __resetFilter(filterKey, filters);

    await loadExperiencesCount();
  };
  /**
   * [method] применить параметры из ссылки
   */
  const __applyDurationQueryParam = (fKey: string, vKey: string, value: string) => {
    const splitValue = value.split(',').filter(Boolean);
    if (splitValue.length === 2) {
      const first = Number(splitValue[0]);
      const second = Number(splitValue[1]);
      // Validate numbers are valid (not NaN, not Infinity)
      if (Number.isFinite(first) && Number.isFinite(second)) {
        filters[fKey][vKey] = [first, second];
        tmpFilters[fKey][vKey] = [first, second];
      }
    }
  };
  const __applyPriceQueryParam = (fKey: string, vKey: string, value: string) => {
    const splitValue = value.split(',').filter(Boolean);
    if (splitValue.length === 2) {
      const first = Number(splitValue[0]);
      const second = Number(splitValue[1]);
      // Validate numbers are valid (not NaN, not Infinity, non-negative)
      if (Number.isFinite(first) && Number.isFinite(second) && first >= 0 && second >= 0) {
        filters[fKey][vKey] = [first, second];
        tmpFilters[fKey][vKey] = [first, second];
      }
    }
  };
  const __applyTourActivitySlugsQueryParam = (fKey: string, vKey: string, value: string) => {
    const slugs = value.split(',').filter((slug) => slug.trim().length > 0);
    if (slugs.length > 0) {
      filters[fKey][vKey] = deepCopy(slugs);
      tmpFilters[fKey][vKey] = deepCopy(slugs);
    }
  };
  const __applyComfortTypeQueryParam = (fKey: string, vKey: string, value: string) => {
    const validComfortValues = Object.values(COMFORT_VALUE);
    const comfortType = value
      .split(',')
      .map((type) => type.trim())
      .filter((type): type is TComfortValue => type.length > 0 && validComfortValues.includes(type as TComfortValue));
    if (comfortType.length > 0) {
      filters[fKey][vKey] = deepCopy(comfortType);
      tmpFilters[fKey][vKey] = deepCopy(comfortType);
    }
  };
  const __applyDefaultQueryParam = (fKey: string, vKey: string, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length > 0 && trimmedValue !== 'null') {
      filters[fKey][vKey] = deepCopy(trimmedValue);
      tmpFilters[fKey][vKey] = deepCopy(trimmedValue);
    }
  };
  const applyQueryParams = () => {
    /**
     * для начала сбрасываем то, что уже есть
     */
    const defaultState = getFiltersDefaultState();
    Object.entries(defaultState).forEach(([fKey, fValue]) => {
      if (fKey !== EExperienceFilterType.SEO_DEFAULT) {
        Object.entries(fValue).forEach(([k, v]) => {
          filters[fKey][k] = deepCopy(v);
          tmpFilters[fKey][k] = deepCopy(v);
        });
      }
    });

    currentPage.value = 1;
    currentSorting.value = ListSortItemValue.RANK;

    const queryParams = new URLSearchParams(window.location.search);
    queryParams.forEach((value, key) => {
      if (key.includes('.')) {
        const [fKey, vKey] = key.split('.');

        // Validate filter key
        if (!Object.values(EExperienceFilterType).includes(fKey as EExperienceFilterType)) {
          return;
        }

        // Validate that the filter exists
        if (!filters[fKey] || !tmpFilters[fKey]) {
          return;
        }

        // Validate property key exists in the filter
        if (!(vKey in filters[fKey])) {
          return;
        }

        switch (vKey) {
          case EExperienceFilterType.DURATION: {
            __applyDurationQueryParam(fKey, vKey, value);
            break;
          }
          case EExperienceFilterType.PRICE: {
            __applyPriceQueryParam(fKey, vKey, value);
            break;
          }
          case EExperienceFilterType.TOUR_ACTIVITY_SLUGS: {
            __applyTourActivitySlugsQueryParam(fKey, vKey, value);
            break;
          }
          case EExperienceFilterType.COMFORT_TYPE: {
            __applyComfortTypeQueryParam(fKey, vKey, value);
            break;
          }
          default: {
            __applyDefaultQueryParam(fKey, vKey, value);
            break;
          }
        }
      }
    });
    /**
     * номер текущей страницы так же достаем из строки браузера
     */
    const routePage = Number(route.query.page ?? 1);
    currentPage.value = Number.isFinite(routePage) && routePage > 0 ? routePage : 1;
    /**
     * тип сортировки тоже берем из строки браузера
     */
    if (route.query.sorting?.length && isSorting(route.query.sorting)) {
      currentSorting.value = route.query.sorting as ListSortItemValue;
    }
  };
  /**
   * [method] метод проверяет активен ли фильтр, сравнивая со значением по умолчанию
   */
  const isFilterActive = (filterKey: EExperienceFilterType) => {
    let gotChanges = false;

    for (const [key, value] of Object.entries(DEFAULT_EXPERIENCE_FILTERS[filterKey])) {
      if (filters[filterKey][key] !== value) {
        gotChanges = true;
      }
    }

    return gotChanges;
  };
  /**
   * выбранные даты для календаря (фильтр по периоду)
   */
  const DATE_FORMAT = 'yyyy-MM-dd';
  const defaultDates = computed<[string, string] | undefined>(() => {
    if (!tmpFilters[EExperienceFilterType.PERIOD].start_date) {
      return ['', ''];
    }

    if (!tmpFilters[EExperienceFilterType.PERIOD].end_date) {
      return [formatDate(tmpFilters[EExperienceFilterType.PERIOD].start_date, DATE_FORMAT), ''];
    }

    return [
      formatDate(tmpFilters[EExperienceFilterType.PERIOD].start_date, DATE_FORMAT),
      formatDate(tmpFilters[EExperienceFilterType.PERIOD].end_date, DATE_FORMAT),
    ];
  });
  /**
   * значение для фильтра Duration по умолчанию
   */
  const maxDuration = ref<number>(1);
  /**
   * значение для фильтра Price по умолчанию
   */
  const histogramPrice = ref<Record<string, number>>({});
  const minPrice = ref<number>(0);
  const maxPrice = ref<number>(1000000);
  /**
   *
   */
  const currencyCode = ref<string>('RUB');
  /**
   *
   */
  const rubricsRef = ref<HTMLElement | null>(null);
  const setRubricsRef = (el: HTMLElement) => {
    rubricsRef.value = el;
  };
  /**
   * [method] Загрузить список активностей для фильтра
   */
  const toursActivities = ref<ToursActivity[]>([]);
  const { labelOf } = useToursActivitiesLabels();
  const loadToursActivities = async () => {
    try {
      const data = await getToursActivities();
      toursActivities.value = createToursActivitiesFromResource(data.data ?? [], labelOf);
    } catch (e) {
      console.error(e);
    }
  };
  /**
   * [method] Загрузить агрегации экспириенсов
   */
  const loadExperienceAggregations = async () => {
    try {
      const data = await getExperienceAggregations({
        query: {
          // country__slug: props.isRegion ? null : props.slug,
          // region__slug: props.isRegion ? props.slug : null,
          type: 'tour',
          persons_count: 1,
          page: 1,
        },
      });

      if (data.data?.duration) {
        maxDuration.value = Number(Object.keys(data.data?.duration).at(-1)) / DAY_HOURS;
      }

      if (data.data?.price) {
        histogramPrice.value = data.data?.price?.histogram as Record<string, number>;
        minPrice.value = data.data?.price?.range?.[0] as number;
        maxPrice.value = data.data?.price?.range?.[1] as number;
      }
    } catch (e) {
      console.error(e);
    }
  };
  /**
   * [method] Загрузить информацию о стране по слагу
   */
  const loadCountryInfo = async () => {
    try {
      const data = await getCountryInfoBySlug({
        path: { countrySlug: countrySlug.value },
      });

      currencyCode.value = data.data?.currency ?? 'RUB';
    } catch (e) {
      console.error(e);
    }
  };
  /**
   * [method] Загрузить интервалы дат для чипсов календаря
   */
  const calendarEventsChips = ref<GetCalendarEventsChipsResponse | null>(null);
  const loadCalendarEventsChips = async () => {
    try {
      const data = await getCalendarEventsChips();
      calendarEventsChips.value = data.data ?? null;
    } catch (e) {
      console.error(e);
    }
  };
  /**
   *
   */
  const init = async () => {
    if (isInitialising.value || isInitialised.value) {
      return;
    }
    isInitialising.value = true;

    if (!categorySlug.value || !countrySlug.value) {
      __redirect();
    }

    /**
     * разбираем параметры строки браузера
     */
    applyQueryParams();

    try {
      /**
       * Для начала выгружаем данные по сгенерированной SEO-категории
       */
      await loadSeoCategory();
      if (seoCategoryId.value) {
        appliedRouteKey.value = currentRouteKey.value;
        /**
         * если данные подгрузились - грузим SEO-инфу для страницы
         */
        await loadSeoInfo();
        /**
         * и список Э
         */
        await loadExperiences();
        /**
         * загрузить инфо страны по слагу (для валюты)
         */
        await loadCountryInfo();
        /**
         *
         */
        await loadToursActivities();
        /**
         *
         */
        await loadExperienceAggregations();
        /**
         *
         */
        await loadCalendarEventsChips();

        isSkeletonActive.value = false;
        isInitialised.value = true;
      }
    } catch (e) {
      console.log(e);
      __redirect();
    } finally {
      isInitialising.value = false;
    }
  };

  const onRouteChange = async () => {
    if (!isInitialised.value) {
      return;
    }

    if (!categorySlug.value || !countrySlug.value) {
      __redirect();

      return;
    }

    const shouldReloadCategory = appliedRouteKey.value !== currentRouteKey.value;

    if (shouldReloadCategory) {
      await loadSeoCategory();

      if (seoCategoryId.value) {
        await loadSeoInfo();
        await loadCountryInfo();
      }

      appliedRouteKey.value = currentRouteKey.value;
    }

    applyQueryParams();
    await resetLists();
  };

  return {
    init,
    onRouteChange,
    isInitialised,
    isExperiencesLoading,
    isExperiencesCountLoading,
    isSkeletonActive,

    seoCategoryId,
    seoCategoryTitle,

    seoData,
    countrySlug,
    breadcrumbs,
    reviewsApiFilters,

    defaultDates,
    maxDuration,
    histogramPrice,
    minPrice,
    maxPrice,
    currencyCode,
    calendarEventsChips,

    filters,
    tmpFilters,
    toursActivities,
    setPeriod,
    setTourActivitySlugs,
    setDuration,
    setPrice,
    setPersons,
    setComfortType,
    setAccommodation,
    setActivityLevel,
    setChildrenAllowed,
    setSorting,
    setPage,
    loadMore,

    onApplyFilter,
    onResetFilter,
    onResetTmpFilter,
    applyQueryParams,
    isFilterActive,

    currentPage,
    currentSorting,
    loadExperiences,
    resetLists,
    experiences,
    experiencesFavorites,
    experiencesCount,
    experiencesTmpCount,
    pageCount,
    appliedFiltersCount,
    appliedTmpFiltersCount,

    setRubricsRef,
  };
});
